import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import Spinner from "@/components/ui/Spinner";
import { JSX, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdCategoryStore } from "./store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import useGlobalStore from "@/store/globalStore";
import { Edit, CheckCircle } from "lucide-react";
import { settingsOptions } from "./component/step-four";

function settingLabel(valueType: string): string {
  return settingsOptions.find((o) => o.value === valueType)?.title ?? valueType;
}

export default function ViewAdTypePreview(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adCategoryId = searchParams.get("id");
  const publisherId = searchParams.get("publisherId");

  const [canvasData, setCanvasData] = useState<any>(null);

  const {
    getAdCategoryDetailData,
    adCategoryDetailData,
    setEditData,
    parametersData,
    fetchParametersData,
    fetchCanvasBuilderData,
  } = useAdCategoryStore(
    useShallow((state) => ({
      getAdCategoryDetailData: state.getAdCategoryDetailData,
      adCategoryDetailData: state.adCategoryDetailData,
      setEditData: state.setEditData,
      parametersData: state.parametersData,
      fetchParametersData: state.fetchParametersData,
      fetchCanvasBuilderData: state.fetchCanvasBuilderData,
    })),
  );

  const { setError, showSuccess } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
      showSuccess: state.showSuccess,
    })),
  );

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        await Promise.all([
          getAdCategoryDetailData(adCategoryId),
          fetchParametersData(),
        ]);
        // After detail data loaded, fetch canvas data for the first ad input of this publisher
        const detail = useAdCategoryStore.getState().adCategoryDetailData;
        const publisherType = detail?.ad_types?.find(
          (at) => at.publisher_details?.id === publisherId,
        );
        const firstInputId = publisherType?.ad_inputs?.[0]?.id;
        if (firstInputId) {
          await fetchCanvasBuilderData(firstInputId);
          setCanvasData(useAdCategoryStore.getState().canvasBuilderData);
        }
      } catch (error: any) {
        setError(error.message ?? "Something went wrong");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [adCategoryId]);

  const publisherAdType = adCategoryDetailData?.ad_types?.find(
    (at) => at.publisher_details?.id === publisherId,
  );

  const adInputs = publisherAdType?.ad_inputs ?? [];
  const publisherName = publisherAdType?.publisher_details?.name ?? "Publisher";

  const handleEditParameters = async () => {
    if (!adCategoryId) return;
    await getAdCategoryDetailData(adCategoryId);
    const detailData = useAdCategoryStore.getState().adCategoryDetailData;
    if (detailData) setEditData(detailData);
    navigate(`/create-ad-categories?id=${adCategoryId}&step=3`);
  };

  const handleEditTemplate = () => {
    if (!adCategoryId) return;
    const detail = useAdCategoryStore.getState().adCategoryDetailData;
    const publisherType = detail?.ad_types?.find(
      (at) => at.publisher_details?.id === publisherId,
    );
    const firstInputId = publisherType?.ad_inputs?.[0]?.id;
    navigate(
      `/create-ad-categories?id=${adCategoryId}&step=5${firstInputId ? `&adInputId=${firstInputId}` : ""}`,
    );
  };

  const handleApprove = async () => {
    try {
      setIsApproving(true);
      // TODO: Replace with actual API call to approve ad type
      await new Promise((resolve) => setTimeout(resolve, 500));
      showSuccess("Ad Type approved successfully");
    } catch (error: any) {
      setError(error.message ?? "Failed to approve ad type");
    } finally {
      setIsApproving(false);
    }
  };

  if (!adCategoryId || !publisherId) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full text-gray-500">
          Ad Type not found.
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <Spinner size={30} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        {/* White header block */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() =>
                navigate(`/ad-categories/publishers?id=${adCategoryId}`)
              }
              className="text-pink-500 hover:text-pink-700 text-2xl leading-none"
              aria-label="Back"
            >
              ←
            </button>
            <span className="text-base text-gray-500">View Ad Type</span>
          </div>
          <h1 className="text-xl font-semibold">
            {adCategoryDetailData?.name ?? "Ad Category"} - {publisherName}
          </h1>
        </div>

        <hr className="border-[#EA33C0]" />

        {adInputs.length === 0 ? (
          <div className="text-gray-500 text-center py-10">
            No ad types found for this publisher.
          </div>
        ) : (
          adInputs.map((input, inputIdx) => {
            const mediaTypeKey = input.media_type?.toLowerCase();
            const paramGroups: any[] = parametersData[mediaTypeKey] ?? [];

            return (
              <div key={inputIdx}>
                {/* Template preview — grey background full-width */}
                <div className="bg-[#ececec] flex items-center justify-center py-16">
                  <div className="bg-white rounded-3xl border-2 border-[#EA33C0] w-72 min-h-[32rem] overflow-hidden shadow-lg">
                    {/* Phone mock top bar */}
                    <div className="border border-pink-200 mx-3 mt-3 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 p-3 border-b border-gray-100">
                        <div className="w-8 h-8 rounded-full bg-pink-500 flex-shrink-0" />
                        <div>
                          <div className="text-xs font-semibold leading-tight">
                            Client Name
                          </div>
                          <div className="text-xs text-gray-400">Sponsored</div>
                        </div>
                        <div className="ml-auto text-gray-400 text-xs">···</div>
                      </div>
                      <div className="px-3 pt-2 text-xs text-gray-500">
                        Primary text placeholder.
                      </div>
                      {/* Image placeholder */}
                      <div className="bg-gray-200 mx-0 mt-2 h-64 flex items-center justify-center">
                        <div className="text-gray-400">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-10 h-10"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="flex items-center justify-between px-3 py-2">
                        <div>
                          <div className="text-xs text-gray-400">
                            destinationurl.com
                          </div>
                          <div className="text-xs font-semibold">
                            Headline Placeholder to go here.
                          </div>
                        </div>
                        <div className="text-xs bg-gray-200 px-2 py-1 rounded">
                          CTA
                        </div>
                      </div>
                    </div>
                    <div className="h-6" />
                  </div>
                </div>

                {/* Ad Specs section — data from canvasBuilderData.parameters (step 5) */}
                <div className="mt-6">
                  <div className="flex">
                    <div className="text-xl font-bold mb-4">Ad Specs</div>
                    <div className="ml-auto self-center">
                      <button
                        onClick={handleEditTemplate}
                        className="text-sm text-gray-400 hover:text-pink-500 underline"
                      >
                        Link to full specs
                      </button>
                    </div>
                  </div>
                  <hr className="border-gray-300 mb-8" />
                  {canvasData?.parameters?.length > 0 ? (
                    <div className="flex items-start gap-16 flex-wrap">
                      {canvasData.parameters.map((param: any) => {
                        const def = param.parameter_definition;
                        const val = param.value;
                        const displayVal =
                          val && typeof val === "object"
                            ? Object.values(val).filter(Boolean).join(", ") ||
                              "—"
                            : (val ?? "—");
                        return (
                          <div key={def?.key ?? param.id}>
                            <div className="text-sm font-bold mb-2">
                              {def?.label ?? "Spec"}
                            </div>
                            <div className="text-sm text-gray-700">
                              {displayVal}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">
                        No ad specs configured.
                      </span>
                      <button
                        onClick={handleEditTemplate}
                        className="text-sm text-gray-400 hover:text-pink-500 underline"
                      >
                        Link to full specs
                      </button>
                    </div>
                  )}
                </div>

                {/* Objectives section */}
                {canvasData?.ad_info_blocks?.objective?.length > 0 && (
                  <div className="mt-6">
                    <div className="text-xl font-bold mb-4">Objectives</div>
                    <hr className="border-gray-300 mb-8" />
                    {canvasData.ad_info_blocks.objective.map((block: any) =>
                      block.components?.map((comp: any) => (
                        <div key={comp.id} className="mb-6">
                          <div className="text-sm font-bold mb-2">
                            {comp.title}
                          </div>
                          {comp.input_type === "dropdown" ? (
                            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#EA33C0] rounded-full text-sm text-gray-700 bg-white min-w-[120px]">
                              <span>
                                {comp.options?.[0]?.label ??
                                  comp.placeholder_text ??
                                  "—"}
                              </span>
                              <svg
                                className="ml-auto w-4 h-4 text-[#EA33C0]"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-96 px-4 py-2 border border-[#EA33C0] rounded-lg text-sm text-gray-400 bg-white">
                              {comp.placeholder_text ?? "Enter valid URL"}
                            </div>
                          )}
                        </div>
                      )),
                    )}
                  </div>
                )}

                {/* Content section */}
                {canvasData?.ad_info_blocks?.content?.length > 0 && (
                  <div className="mt-6">
                    <div className="text-xl font-bold mb-4">Content</div>
                    <hr className="border-gray-300 mb-8" />
                    <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                      {canvasData.ad_info_blocks.content.map((block: any) =>
                        block.components?.map((comp: any) => {
                          const isTextarea = comp.input_type === "textarea";
                          const isMedia =
                            comp.input_type === "media" ||
                            comp.input_type === "image";
                          const charLimit =
                            comp.character_limit ?? comp.soft_limit;
                          if (isMedia) {
                            return (
                              <div key={comp.id} className="col-span-2">
                                <div className="text-sm font-bold mb-1">
                                  {comp.title}
                                </div>
                                <div className="text-sm text-gray-400 mb-3">
                                  1 Image
                                </div>
                                <div className="w-fit">
                                  <div className="text-sm text-gray-400 mb-1">
                                    1
                                  </div>
                                  <div className="w-40 h-32 border border-gray-300 rounded-xl bg-white flex flex-col items-center justify-center text-gray-400">
                                    <span className="text-2xl leading-none mb-1">
                                      +
                                    </span>
                                    <span className="text-xs">
                                      Image (30MB max)
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={comp.id}>
                              <div className="text-sm font-bold mb-2">
                                {comp.title}
                              </div>
                              {isTextarea ? (
                                <div className="relative">
                                  <div className="w-full h-32 px-4 py-3 border border-[#EA33C0] rounded-xl text-sm text-gray-400 bg-white">
                                    {comp.placeholder_text ??
                                      "Placeholder text...."}
                                  </div>
                                  {charLimit && (
                                    <div className="text-xs text-gray-400 text-right mt-1">
                                      0/{charLimit}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="relative">
                                  <div className="w-full px-4 py-2 border border-[#EA33C0] rounded-lg text-sm text-gray-400 bg-white">
                                    {comp.placeholder_text ??
                                      "Placeholder text...."}
                                  </div>
                                  {charLimit && (
                                    <div className="text-xs text-gray-400 text-right mt-1">
                                      0/{charLimit}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }),
                      )}
                    </div>
                  </div>
                )}

                {/* Ad Type Parameters */}
                <div className="mt-6">
                  <div className="text-xl font-bold mb-3">
                    Ad Type Parameters
                  </div>
                  <hr className="border-gray-300 mb-4" />

                  {paramGroups.map((group) => {
                    const matchedParam = input.parameters?.find(
                      (p) => p.parameter_definition?.key === group.key,
                    );

                    const valueType =
                      matchedParam?.value_type ?? "single_publisher";
                    const storedValue: Record<string, any> =
                      matchedParam?.value &&
                      typeof matchedParam.value === "object"
                        ? (matchedParam.value as Record<string, any>)
                        : {};

                    return (
                      <div className="mt-6" key={group.key}>
                        <div className="text-base font-bold mb-1">
                          {group.label}
                        </div>
                        <hr className="border-gray-300 mb-3" />
                        <div className="flex gap-12">
                          {/* Setting */}
                          <div>
                            <div className="text-xs text-gray-400 mb-1">
                              Setting
                            </div>
                            <div className="text-sm">
                              {settingLabel(valueType)}
                            </div>
                          </div>
                          {/* Dynamic fields */}
                          {group.fields.map((field: any) => {
                            const rawVal = storedValue[field.key];
                            const displayVal =
                              rawVal !== undefined &&
                              rawVal !== null &&
                              rawVal !== ""
                                ? String(rawVal)
                                : "#";
                            return (
                              <div key={field.key}>
                                <div className="text-xs text-gray-400 mb-1">
                                  {field.label}
                                </div>
                                <div className="text-sm">{displayVal}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Custom parameters */}
                  {input.custom_parameters?.map((cp: any, cpIdx: number) => (
                    <div
                      key={cpIdx}
                      className="mt-6 bg-gray-200 rounded-lg p-4"
                    >
                      <div className="text-base font-bold mb-1">
                        Custom Parameter #{cpIdx + 1}
                      </div>
                      <hr className="border-gray-300 mb-3" />
                      <div className="flex gap-12">
                        <div>
                          <div className="text-xs text-gray-400 mb-1">
                            Setting
                          </div>
                          <div className="text-sm">
                            {settingLabel(cp.setting ?? "single_publisher")}
                          </div>
                        </div>
                        {cp.name && (
                          <div>
                            <div className="text-xs text-gray-400 mb-1">
                              {cp.type ?? "Format"}
                            </div>
                            <div className="text-sm">
                              {Array.isArray(cp.values)
                                ? cp.values.join(", ") || "—"
                                : (cp.value ?? cp.name ?? "—")}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Specs & Due Dates from parameters */}
                  {(() => {
                    const specsParam = input.parameters?.find(
                      (p) => p.parameter_definition?.key === "specs",
                    );
                    const dueDatesParam = input.parameters?.find(
                      (p) => p.parameter_definition?.key === "due_dates",
                    );

                    return (
                      <>
                        {specsParam && (
                          <div className="mt-6">
                            <div className="text-base font-bold mb-1">
                              Specs
                            </div>
                            <hr className="border-gray-300 mb-3" />
                            <div className="flex gap-12">
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Setting
                                </div>
                                <div className="text-sm">
                                  {settingLabel(
                                    specsParam.value_type ?? "single_publisher",
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Publisher Specs
                                </div>
                                <div className="text-sm">
                                  {(specsParam.value as any)?.publisher_specs ??
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {dueDatesParam && (
                          <div className="mt-6">
                            <div className="text-base font-bold mb-1">
                              Due Dates
                            </div>
                            <hr className="border-gray-300 mb-3" />
                            <div className="flex gap-12">
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Setting
                                </div>
                                <div className="text-sm">
                                  {settingLabel(
                                    dueDatesParam.value_type ??
                                      "single_publisher",
                                  )}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Due to Media
                                </div>
                                <div className="text-sm">
                                  {(dueDatesParam.value as any)?.due_to_media ??
                                    "N/A"}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-400 mb-1">
                                  Due to Publisher
                                </div>
                                <div className="text-sm">
                                  {(dueDatesParam.value as any)
                                    ?.due_to_publisher ?? "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  <hr className="border-gray-300 mt-8 mb-6" />

                  {/* Bottom action buttons */}
                  <div className="flex items-center gap-3">
                    <Button type="secondary" onClick={handleEditParameters}>
                      Edit Parameters
                    </Button>
                    <Button
                      type="secondary"
                      leadingIcon={<Edit className="w-4 h-4" />}
                      onClick={handleEditTemplate}
                    >
                      Edit Template
                    </Button>
                    <Button
                      type="primary"
                      leadingIcon={<CheckCircle className="w-4 h-4" />}
                      isLoading={isApproving}
                      onClick={handleApprove}
                    >
                      Approve Ad Type
                    </Button>
                  </div>
                </div>

                {inputIdx < adInputs.length - 1 && (
                  <hr className="border-gray-200 mt-4" />
                )}
              </div>
            );
          })
        )}
      </div>
    </Layout>
  );
}
